import { forwardRef, useId } from 'react';
import { AlertCircle } from 'lucide-react';
import cn from '../../utils/cn';

export function Field({ label, hint, error, required, children, htmlFor }) {
  return (
    <div className="field">
      {label && (
        <label className="field__label" htmlFor={htmlFor}>
          {label}
          {required && <span className="field__req"> *</span>}
        </label>
      )}
      {children}
      {error ? (
        <span className="field__error">
          <AlertCircle size={13} />
          {error}
        </span>
      ) : (
        hint && <span className="field__hint">{hint}</span>
      )}
    </div>
  );
}

export const Input = forwardRef(function Input({ label, hint, error, required, className, id, ...rest }, ref) {
  const generated = useId();
  const inputId = id || generated;
  const input = <input ref={ref} id={inputId} className={cn('input', error && 'is-invalid', className)} {...rest} />;
  if (!label && !hint && !error) return input;
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={inputId}>
      {input}
    </Field>
  );
});

export const Textarea = forwardRef(function Textarea({ label, hint, error, className, id, ...rest }, ref) {
  const generated = useId();
  const inputId = id || generated;
  const el = <textarea ref={ref} id={inputId} className={cn('textarea', error && 'is-invalid', className)} {...rest} />;
  if (!label && !hint && !error) return el;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      {el}
    </Field>
  );
});

export const Select = forwardRef(function Select({ label, hint, error, options = [], className, id, children, ...rest }, ref) {
  const generated = useId();
  const inputId = id || generated;
  const el = (
    <select ref={ref} id={inputId} className={cn('select', error && 'is-invalid', className)} {...rest}>
      {children || options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  );
  if (!label && !hint && !error) return el;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      {el}
    </Field>
  );
});

export function Checkbox({ label, indeterminate, className, ...rest }) {
  return (
    <label className={cn('checkbox', className)}>
      <input
        type="checkbox"
        ref={(el) => {
          if (el) el.indeterminate = Boolean(indeterminate);
        }}
        {...rest}
      />
      {label && <span>{label}</span>}
    </label>
  );
}

export function Switch({ label, description, ...rest }) {
  return (
    <label className="row" style={{ gap: 'var(--sp-3)', cursor: 'pointer' }}>
      <input type="checkbox" className="switch" {...rest} />
      <span className="stack" style={{ gap: 2 }}>
        <span style={{ fontSize: 'var(--fs-cell)', fontWeight: 550 }}>{label}</span>
        {description && <span className="meta">{description}</span>}
      </span>
    </label>
  );
}

export function RadioCard({ checked, label, description, ...rest }) {
  return (
    <label className={cn('radio', checked && 'is-selected')}>
      <input type="radio" checked={checked} readOnly={!rest.onChange} {...rest} />
      <span className="stack" style={{ gap: 2 }}>
        <span style={{ fontSize: 'var(--fs-cell)', fontWeight: 550 }}>{label}</span>
        {description && <span className="meta">{description}</span>}
      </span>
    </label>
  );
}

export default Field;
